import { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import API_BASE_URL from '../../config';



const colors = {
  weight: '#84cc16',
  neck: '#ef4444',
  shoulders: '#3b82f6',
  chest: '#f97316',
  biceps: '#6366f1',
  belly: '#10b981',
  waist: '#8b5cf6',
  hip: '#f43f5e',
  calf: '#14b8a6',
  thighs: '#facc15'
};

const AccordionSection = ({ title, isOpen, onToggle, children, contentClassName = "px-6 pb-6 pt-0" }) => (
  <div className="bg-[#181818] border border-[#2b2b2b] rounded-xl overflow-hidden">
    <button
      onClick={onToggle}
      className="w-full flex justify-between items-center px-6 py-4 font-medium text-left text-white hover:bg-[#2b2b2b] focus:outline-none"
      aria-expanded={isOpen}
    >
      <span>{title}</span>
      <svg
        className={`w-5 h-5 text-lime-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : 'rotate-0'
          }`}
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
      </svg>
    </button>
    {isOpen && <div className={contentClassName}>{children}</div>}
  </div>
);

const WeightTrackingTab = ({ clientRecords, assignedMembers }) => {
  const [members, setMembers] = useState([]);

  useEffect(() => {
    if (assignedMembers) {
      const allUsers = assignedMembers.map((u, idx) => ({
        id: u.id || (idx + 1),
        name: u.name,
        username: u.username
      }));
      setMembers(allUsers);
      return;
    }

    const fetchGymUsers = async () => {
      const userInfo = JSON.parse(localStorage.getItem('fitscan_user'));

      try {
        const response = await fetch(`${API_BASE_URL}/api/FitnessTracking/Userslist`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${JSON.parse(localStorage.getItem('accessToken') || '{}').token || ''}`
          },
          body: JSON.stringify(userInfo.username), // send raw string in body
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        const allUsers = data.allGymUsers.map((u, idx) => ({
          id: idx + 1,
          name: `${u.firstName} ${u.lastName}`,
          username: u.username
        }));

        // --- START DUMMY DATA ---
        const dummyMembers = [
          {
            id: 'dummy-1', name: 'Demo Member 1', username: 'demo1', records: [
              { date: '2024-04-01', weight: 85, neck: 16, shoulders: 48, chest: 42, biceps: 15, belly: 36, waist: 34, hip: 40, calf: 14, thighs: 24 },
              { date: '2024-04-15', weight: 82, neck: 15.5, shoulders: 47, chest: 41, biceps: 15.2, belly: 35, waist: 33, hip: 39, calf: 14.2, thighs: 23.5 }
            ]
          },
          { id: 'dummy-2', name: 'Demo Member 2', username: 'demo2', records: [] }
        ];
        setMembers([...dummyMembers, ...allUsers]);
        // --- END DUMMY DATA ---
      } catch (err) {
        console.error(err);
        // Fallback dummy data if API fails
        const dummyMembers = [
          {
            id: 'dummy-1', name: 'Demo Member 1', username: 'demo1', records: [
              { date: '2024-04-01', weight: 85, neck: 16, shoulders: 48, chest: 42, biceps: 15, belly: 36, waist: 34, hip: 40, calf: 14, thighs: 24 },
              { date: '2024-04-15', weight: 82, neck: 15.5, shoulders: 47, chest: 41, biceps: 15.2, belly: 35, waist: 33, hip: 39, calf: 14.2, thighs: 23.5 }
            ]
          },
          { id: 'dummy-2', name: 'Demo Member 2', username: 'demo2', records: [] }
        ];
        setMembers(dummyMembers);
      }
    };
    fetchGymUsers();

  }, [assignedMembers]);


  const formatDate = (dateStr) => {
    if (!dateStr || typeof dateStr !== 'string') {
      console.warn('Invalid dateStr:', dateStr);
      return '-';
    }

    // Support both '-' and '/' separators if needed
    let parts = [];
    if (dateStr.includes('-')) parts = dateStr.split('-');
    else if (dateStr.includes('/')) parts = dateStr.split('/');
    else {
      console.warn('Unexpected date format:', dateStr);
      return '-';
    }

    if (parts.length !== 3) {
      console.warn('Date parts length invalid:', parts);
      return '-';
    }

    const [year, month, day] = parts;
    if (!year || !month || !day) return '-';

    // Optional: pad day/month with zero if needed
    const dayPadded = day.length === 1 ? '0' + day : day;
    const monthPadded = month.length === 1 ? '0' + month : month;

    return `${dayPadded}/${monthPadded}/${year}`;
  };



  const [selectedMember, setSelectedMember] = useState(null);
  const [newRecord, setNewRecord] = useState({
    weight: '0',
    neck: '0',
    shoulders: '0',
    chest: '0',
    biceps: '0',
    belly: '0',
    waist: '0',
    hip: '0',
    calf: '0',
    thighs: '0'
  });
  const fitscan_user = JSON.parse(localStorage.getItem('fitscan_user'));

  const [openSection, setOpenSection] = useState(null);
  const [selectedMetrics, setSelectedMetrics] = useState(['weight']);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [timeRange, setTimeRange] = useState('30D');

  const parseCustomDate = (dStr) => {
    if (!dStr) return new Date(0);
    const parts = dStr.split('/');
    if (parts.length === 3) return new Date(parts[2], parts[1] - 1, parts[0]);
    return new Date(dStr);
  };

  const getFilteredRecords = () => {
    if (!selectedMember?.records) return [];

    if (timeRange === 'Overall') {
      return [...selectedMember.records].sort((a, b) => parseCustomDate(a.date) - parseCustomDate(b.date));
    }

    const now = new Date();
    now.setHours(23, 59, 59, 999);

    let days = 30;
    if (timeRange === '7D') days = 7;
    else if (timeRange === '30D') days = 30;
    else if (timeRange === '90D') days = 90;

    const cutoff = new Date();
    cutoff.setDate(now.getDate() - days);
    cutoff.setHours(0, 0, 0, 0);

    return selectedMember.records.filter(record => {
      const recordDate = parseCustomDate(record.date);
      return recordDate >= cutoff;
    }).sort((a, b) => parseCustomDate(a.date) - parseCustomDate(b.date));
  };

  const filteredRecords = getFilteredRecords();

  const toggleSection = async (section) => {
    if (section === 'progress' && selectedMember && !selectedMember.records) {
      try {
        const username = selectedMember.username;
        const res = await fetch(`${API_BASE_URL}/api/FitnessTracking/records`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${JSON.parse(localStorage.getItem('accessToken') || '{}').token || ''}`
          },
          body: JSON.stringify({ username }),
        });

        const records = await res.json();
        const cleanedRecords = records.map((r) => {
          const cleaned = {};
          for (const k in r) {
            if (k === "date") {
              cleaned[k] = formatDate(r[k]);
            } else {
              cleaned[k] = typeof r[k] === 'number' ? r[k] : parseFloat(r[k]);
            }
          }
          return cleaned;
        });

        setSelectedMember(prev => ({ ...prev, records: cleanedRecords }));
      } catch (err) {
        console.error('Failed to fetch progress data:', err);
      }
    }
    setOpenSection((prev) => (prev === section ? null : section));
  };


  const handleAddRecord = async () => {
    if (!selectedMember) return;

    const recordData = {
      ...Object.fromEntries(
        Object.entries(newRecord).map(([key, val]) => [key, val ? parseFloat(val) : 0])
      ),
      Username: selectedMember.username,
      EnteredBy: fitscan_user.username,
      Date: isEditing && editingId ? selectedMember.records.find(r => r.id === editingId)?.date : new Date().toISOString().split('T')[0],
      Active: true
    };

    if (isEditing) {
      try {
        await fetch(`${API_BASE_URL}/api/FitnessTracking/update`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            'Authorization': `Bearer ${JSON.parse(localStorage.getItem('accessToken') || '{}').token || ''}`
          },
          body: JSON.stringify({ ...recordData, id: editingId, Date: new Date(recordData.Date).toISOString() })
        });

        const updatedRecords = selectedMember.records.map(r => {
          if (r.id === editingId) {
            const cleaned = { ...r };
            for (const key in newRecord) {
              cleaned[key.toLowerCase()] = newRecord[key] ? parseFloat(newRecord[key]) : 0;
            }
            return cleaned;
          }
          return r;
        });
        setSelectedMember({ ...selectedMember, records: updatedRecords });
        setIsEditing(false);
        setEditingId(null);
      } catch (err) {
        console.error('Update failed:', err);
      }
    } else {
      try {
        const res = await fetch(`${API_BASE_URL}/api/FitnessTracking/AddFitnessTracking`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            'Authorization': `Bearer ${JSON.parse(localStorage.getItem('accessToken') || '{}').token || ''}`
          },
          body: JSON.stringify(recordData)
        });

        const added = await res.json();
        const cleanedAdded = {};
        for (const k in added) {
          const lowerK = k.toLowerCase();
          if (lowerK === 'date') {
            const dateVal = added[k].includes('T') ? added[k].split('T')[0] : added[k];
            cleanedAdded.date = formatDate(dateVal);
          } else if (lowerK === 'id') {
            cleanedAdded.id = added[k];
          } else if (!excludedKeys.includes(lowerK)) {
            cleanedAdded[lowerK] = typeof added[k] === 'number' ? added[k] : parseFloat(added[k]);
          }
        }
        const updated = { ...selectedMember, records: [...(selectedMember.records || []), cleanedAdded] };
        setSelectedMember(updated);
      } catch (err) {
        console.error(err);
      }
    }

    setNewRecord({
      weight: '', neck: '', shoulders: '', chest: '', biceps: '',
      belly: '', waist: '', hip: '', calf: '', thighs: ''
    });
    setOpenSection('progress');
  };

  const handleEditClick = (record) => {
    setNewRecord({
      weight: record.weight?.toString() || '',
      neck: record.neck?.toString() || '',
      shoulders: record.shoulders?.toString() || '',
      chest: record.chest?.toString() || '',
      biceps: record.biceps?.toString() || '',
      belly: record.belly?.toString() || '',
      waist: record.waist?.toString() || '',
      hip: record.hip?.toString() || '',
      calf: record.calf?.toString() || '',
      thighs: record.thighs?.toString() || ''
    });
    setIsEditing(true);
    setEditingId(record.id);
    setOpenSection('addRecord');
  };

  const handleDeleteRecord = async (id) => {
    if (!window.confirm('Are you sure you want to delete this record?')) return;
    try {
      await fetch(`${API_BASE_URL}/api/FitnessTracking/${id}`, {
        method: "DELETE",
        headers: {
          'Authorization': `Bearer ${JSON.parse(localStorage.getItem('accessToken') || '{}').token || ''}`
        }
      });
      setSelectedMember(prev => ({
        ...prev,
        records: prev.records.filter(r => r.id !== id)
      }));
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const handleMemberChange = async (e) => {
    const memberId = e.target.value;
    const member = members.find(m => String(m.id) === String(memberId));
    setSelectedMember(member);
    if (!member) return;

    if (String(memberId).startsWith('dummy')) {
      setOpenSection('addRecord');
      return;
    }

    try {
      const username = member.username;

      const res = await fetch(`${API_BASE_URL}/api/FitnessTracking/records`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${JSON.parse(localStorage.getItem('accessToken') || '{}').token || ''}`
        },
        body: JSON.stringify({ username }),
      }
      );

      const records = await res.json();
      const cleanedRecords = records.map((r) => {
        const cleaned = {};
        for (const k in r) {
          if (k === "date") {
            cleaned[k] = formatDate(r[k]);  // You format here sometimes
          } else {
            cleaned[k] = typeof r[k] === 'number' ? r[k] : parseFloat(r[k]);
          }
        }
        return cleaned;
      });


      const finalRecords = cleanedRecords.map((r) => ({

        ...r,
        date: r.date,

      }));

      setSelectedMember({ ...member, records: finalRecords });
      setOpenSection('addRecord');
    } catch (err) {
      console.error(err);
    }
  };

  // Keys to track & display in graph and table
  const excludedKeys = ['id', 'date', 'Date', 'username', 'Username', 'enteredby', 'EnteredBy', 'active', 'Active'];
  const measurementKeys = selectedMember?.records?.length
    ? Object.keys(selectedMember.records[0] || {}).filter(k => !excludedKeys.includes(k))
    : [];

  return (
    <div className="space-y-6 max-w-5xl mx-auto px-4">
      <h1 className="text-3xl font-bold mb-6 text-center text-white">Fitness Tracking</h1>

      <AccordionSection
        title="Member"
        isOpen={openSection === 'selectMember'}
        onToggle={() => toggleSection('selectMember')}
      >
        <select
          value={selectedMember?.id || ''}
          onChange={handleMemberChange}
          className="w-full bg-[#222] border border-[#2b2b2b] text-white rounded-lg px-4 py-2"
        >
          <option value="" disabled>--Select--</option>
          {members.map((member) => (
            <option key={member.id} value={member.id}>{member.name}</option>
          ))}
        </select>
      </AccordionSection>

      <AccordionSection
        title={selectedMember ? `Add Measurements for ${selectedMember.name}` : 'Add Measurements'}
        isOpen={openSection === 'addRecord'}
        onToggle={() => toggleSection('addRecord')}
      >
        {selectedMember ? (
          <div className="space-y-4">
            {Object.keys(newRecord).map((field) => (
              <div key={field}>
                <label className="block text-sm text-gray-400 capitalize mb-1">{field === 'weight' ? 'Weight (kg)' : `${field.charAt(0).toUpperCase() + field.slice(1)} (cm)`}</label>
                <input
                  type="number"
                  value={newRecord[field]}
                  onChange={(e) =>
                    setNewRecord({ ...newRecord, [field]: e.target.value })
                  }
                  className="w-full bg-[#222] border border-[#2b2b2b] rounded-lg px-4 py-2"
                  placeholder={field === 'weight' ? 'Enter weight in kg' : `Enter ${field} in cm`}
                />
              </div>
            ))}
            <div className="flex gap-4">
              <button
                onClick={handleAddRecord}
                className="flex-1 bg-lime-400 text-black px-4 py-2 rounded-lg font-medium hover:bg-lime-500 transition"
              >
                {isEditing ? 'Update Entry' : 'Record Entry'}
              </button>
              {isEditing && (
                <button
                  onClick={() => {
                    setIsEditing(false);
                    setEditingId(null);
                    setNewRecord({
                      weight: '', neck: '', shoulders: '', chest: '', biceps: '',
                      belly: '', waist: '', hip: '', calf: '', thighs: ''
                    });
                  }}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500 transition"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        ) : (
          <p className="text-gray-400">Please select a member first.</p>
        )}
      </AccordionSection>

      <AccordionSection
        title={selectedMember ? `${selectedMember.name}'s Weight Progress` : 'Weight Progress'}
        isOpen={openSection === 'progress'}
        onToggle={() => toggleSection('progress')}
        contentClassName="px-0 sm:px-6 pb-6 pt-0"
      >
        {selectedMember ? (
          <>
            <div className="w-full overflow-hidden pt-4">
              {/* Controls Container */}
              <div className="flex justify-between items-center gap-2 mb-6 px-4">
                {/* Measurement Selector Dropdown (Left) */}
                <div className="relative">
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="flex items-center gap-2 bg-[#161616] border border-gray-800 rounded-xl px-3 py-2 text-xs sm:text-sm font-medium text-white hover:bg-[#1a1a1a] transition-colors focus:outline-none focus:ring-1 focus:ring-[#6CD800]"
                  >
                    <span
                      className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full"
                      style={{ backgroundColor: colors[selectedMetrics[0]] || '#8884d8' }}
                    />
                    <span className="truncate max-w-[60px] sm:max-w-none">
                      {selectedMetrics[0] ? selectedMetrics[0].charAt(0).toUpperCase() + selectedMetrics[0].slice(1) : 'Select'}
                    </span>
                    <svg
                      className={`w-3 h-3 sm:w-4 sm:h-4 ml-1 text-gray-400 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {isDropdownOpen && (
                    <div className="absolute left-0 mt-2 w-40 sm:w-48 bg-[#181818]/80 backdrop-blur-md border border-gray-800 rounded-xl shadow-xl overflow-hidden py-1 z-20">
                      {measurementKeys.map(key => (
                        <button
                          key={key}
                          onClick={() => {
                            setSelectedMetrics([key]);
                            setIsDropdownOpen(false);
                          }}
                          className={`w-full text-left px-4 py-2.5 text-xs sm:text-sm transition-colors flex items-center gap-2 ${selectedMetrics.includes(key)
                            ? 'bg-[#6CD800]/10 text-[#6CD800] font-medium'
                            : 'text-gray-300 hover:bg-[#222] hover:text-white'
                            }`}
                        >
                          <span
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: colors[key] || '#8884d8' }}
                          />
                          {key.charAt(0).toUpperCase() + key.slice(1)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Time Range Toggle (Right) */}
                <div className="bg-[#161616] p-1 rounded-xl flex gap-1">
                  {['7D', '30D', '90D', 'Overall'].map((range) => (
                    <button
                      key={range}
                      onClick={() => setTimeRange(range)}
                      className={`px-2 sm:px-4 py-1.5 rounded-lg text-[10px] sm:text-xs font-bold transition-all duration-200 uppercase tracking-wider ${timeRange === range
                          ? 'bg-[#6CD800] text-black shadow-lg scale-105'
                          : 'text-gray-500 hover:text-white hover:bg-[#222]'
                        }`}
                    >
                      {range === 'Overall' ? 'ALL' : range}
                    </button>
                  ))}
                </div>
              </div>

              <div className="w-full aspect-video min-h-[300px] px-0 sm:px-6">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={filteredRecords} margin={{ top: 10, right: 0, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2b2b2b" vertical={false} />
                    <XAxis
                      dataKey="date"
                      tickFormatter={formatDate}
                      tick={{ fill: '#a1a1aa', fontSize: 12 }}
                      stroke="#2b2b2b"
                      minTickGap={15}
                      padding={{ left: 0, right: 0 }}
                    />
                    <YAxis
                      width={30}
                      tick={{ fill: '#a1a1aa', fontSize: 12 }}
                      stroke="#2b2b2b"
                      domain={['auto', 'auto']}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip contentStyle={{ backgroundColor: '#181818', borderColor: '#2b2b2b', borderRadius: '0.5rem', color: '#fff' }} />
                    <Legend wrapperStyle={{ color: 'white', paddingTop: '10px', fontSize: '12px' }} />
                    {measurementKeys.filter(key => selectedMetrics.includes(key)).map(key => (
                      <Line
                        key={key}
                        type="monotone"
                        dataKey={key}
                        stroke={colors[key] || '#8884d8'}
                        strokeWidth={3}
                        activeDot={{ r: 6, strokeWidth: 0 }}
                        name={key.charAt(0).toUpperCase() + key.slice(1)}
                        connectNulls
                        animationDuration={1000}
                        animationEasing="ease-in-out"
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>








            <div className="mt-6 overflow-x-auto rounded-lg border border-[#2b2b2b] mx-0 sm:mx-6">
              <table className="min-w-full divide-y divide-[#2b2b2b] text-white">
                <thead className="bg-[#222]">
                  <tr>
                    <th className="px-4 py-2 text-left text-sm font-semibold">Date</th>
                    {measurementKeys.map(key => (
                      <th
                        key={key}
                        className="px-4 py-2 text-left text-sm font-semibold capitalize"
                        style={{ color: colors[key] || 'white' }}
                      >
                        {key}
                      </th>
                    ))}
                    <th className="px-4 py-2 text-right text-sm font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#2b2b2b] bg-[#181818]">
                  {Array.isArray(selectedMember?.records) && selectedMember.records.map((record, idx) => (
                    <tr key={idx} className="hover:bg-[#2b2b2b]">
                      <td className="px-4 py-2 whitespace-nowrap">{formatDate(record.date)}</td>
                      {measurementKeys.map(key => (
                        <td key={key} className="px-4 py-2 whitespace-nowrap">
                          {record[key] != null && !isNaN(record[key]) ? record[key] : '-'}
                        </td>
                      ))}
                      <td className="px-4 py-2 whitespace-nowrap text-right space-x-2">
                        <button
                          onClick={() => handleEditClick(record)}
                          className="text-[#6CD800] hover:text-lime-300 text-xs font-medium"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteRecord(record.id)}
                          className="text-red-400 hover:text-red-300 text-xs font-medium"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          <p className="text-gray-400">Please select a member to view progress.</p>
        )}
      </AccordionSection>
    </div>
  );
};

export default WeightTrackingTab;
